import { useTranslation } from '@/i18n';
import { useGlobalContainerRef } from '@/wc/helper/ref';
import type { AdminInvoiceReportColumn } from '@/features/admin_invoice_reports/types';
import {
  ActionMenu,
  IconButton,
  IconMoreVert,
  TextLink,
  Tooltip,
} from '@moneyforward/ap-frontend-components';
import classnames from 'classnames/bind';
import { FC, memo, useMemo } from 'react';
import styles from './ActionCell.module.scss';

const cx = classnames.bind(styles);

export type ActionCellProps = {
  record: AdminInvoiceReportColumn;
  onCancel?: (record: AdminInvoiceReportColumn) => void;
  onDelete?: (record: AdminInvoiceReportColumn) => void;
};

export const ActionCell: FC<ActionCellProps> = memo(
  ({ record, onCancel, onDelete }) => {
    const { t } = useTranslation();
    const container = useGlobalContainerRef();
    const dropDownProps = useMemo(() => ({ destroyPopupOnHide: true }), []);

    const menuItems = useMemo(() => {
      const items = [];

      if (record.cancel_action.visible) {
        const isDisabled = !record.cancel_action.allowed;
        const reason = record.cancel_action.reason;

        const label =
          isDisabled && reason ? (
            <Tooltip
              title={reason}
              overlayClassName={cx(styles['actionCellTooltip'])}
              getPopupContainer={container}
            >
              <div>
                <span className={cx('actionLabelDisabled')}>
                  {t('admin_invoice_reports.actions.cancel')}
                </span>
              </div>
            </Tooltip>
          ) : (
            <span className={cx('actionLabel')}>
              {t('admin_invoice_reports.actions.cancel')}
            </span>
          );

        items.push({
          key: 'cancel',
          label,
          disabled: isDisabled,
          onClick: isDisabled ? undefined : () => onCancel?.(record),
        });
      }

      if (record.delete_action.visible) {
        const isDisabled = !record.delete_action.allowed;
        const reason = record.delete_action.reason;

        const label =
          isDisabled && reason ? (
            <Tooltip
              title={reason}
              overlayClassName={cx(styles['actionCellTooltip'])}
              getPopupContainer={container}
            >
              <div>
                <span className={cx('actionLabelDisabled')}>
                  {t('admin_invoice_reports.actions.delete')}
                </span>
              </div>
            </Tooltip>
          ) : (
            <span className={cx('actionLabel')}>
              {t('admin_invoice_reports.actions.delete')}
            </span>
          );

        items.push({
          key: 'delete',
          label,
          disabled: isDisabled,
          onClick: isDisabled ? undefined : () => onDelete?.(record),
        });
      }

      return items;
    }, [record, onCancel, onDelete, t, container]);

    return (
      <div
        className={cx(styles['container'])}
        onClick={(e) => e.stopPropagation()}
      >
        <TextLink href={record.detail_path}>
          {t('admin_invoice_reports.table.columns.detail')}
        </TextLink>
        <ActionMenu
          type='custom'
          items={menuItems}
          dropDownProps={dropDownProps}
          getPopupContainer={container}
        >
          <IconButton size='xs' inVisible>
            <IconMoreVert size={20} />
          </IconButton>
        </ActionMenu>
      </div>
    );
  }
);

ActionCell.displayName = 'ActionCell';
